import React, { useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';
import { useQuery } from '@tanstack/react-query';
import { fetchBanner, fetchTemporaryBadge } from '../api/mobileapi';
import { TemporaryBadgeData } from '../api/types';
import { AccessSituationBanner } from '../components/AccessSituationBanner/AccessSituationBanner';
import { DevelopmentButtons } from '../components/DevelopmentButtons/DevelopmentButtons';
import { GlobalError } from '../components/GlobalError/GlobalError';
import { HideInProduction } from '../components/HideInProduction/HideInProduction';
import { useHomeData } from '../components/LoadHomeData/HomeDataProvider';
import { TemporaryBadge } from '../components/TemporaryBadge/TemporaryBadge';
import { useTranslations } from '../components/Translations/TranslationsProvider';
import { AdamsError } from '../utils/errors';

export const Homepage = () => {
    const { translations } = useTranslations();
    const { currentLanguage, homepageData, userPicture } = useHomeData();
    const [isBannerVisible, setIsBannerVisible] = useState(true);

    const {
        data: badgeData,
        error: badgeError,
        isLoading,
        fetchStatus,
    } = useQuery<TemporaryBadgeData, AdamsError>(
        ['temporaryBadge'],
        () => fetchTemporaryBadge({ language: currentLanguage }),
        {
            enabled: homepageData?.siteAccessSituation,
        }
    );

    const { data: bannerData, isLoading: isBannerLoading } = useQuery(['banner', currentLanguage], () =>
        fetchBanner({ language: currentLanguage })
    );

    const isBannerRead = (bannerId: number) => {
        const readBanners = JSON.parse(localStorage.getItem('banners') || '[]');
        return readBanners.includes(bannerId);
    };

    if (badgeError) {
        return <GlobalError error={badgeError} translations={translations} />;
    }

    if ((isLoading && fetchStatus !== 'idle') || !homepageData || !badgeData || isBannerLoading) {
        return <LinearProgress />;
    }

    const closeBanner = (bannerId: number) => {
        setIsBannerVisible(false);
        const readBanners = JSON.parse(localStorage.getItem('banners') || '[]');
        readBanners.push(bannerId);
        localStorage.setItem('banners', JSON.stringify(readBanners));
    };
    return (
        <>
            {bannerData && !isBannerRead(bannerData.bannerID) && isBannerVisible && (
                <Alert icon={false} severity="info" onClose={() => closeBanner(bannerData.bannerID)} sx={{ mb: 1 }}>
                    {bannerData.bannerText}
                </Alert>
            )}
            <Card sx={{ boxShadow: 3, maxWidth: 600, margin: 'auto' }}>
                <CardContent sx={{ padding: 0 }}>
                    <AccessSituationBanner
                        accessSituation={homepageData?.siteAccessSituation}
                        description={homepageData.siteDescription}
                    />

                    <Box sx={{ padding: '0 1rem', marginTop: '-4rem' }}>
                        <TemporaryBadge
                            accessSituation={homepageData?.siteAccessSituation}
                            badgeData={badgeData}
                            noAccessLink={homepageData?.siteAccessLink}
                            userData={homepageData}
                            userPicture={userPicture}
                            translations={translations}
                        />
                    </Box>
                </CardContent>
            </Card>
            <HideInProduction>
                <DevelopmentButtons />
            </HideInProduction>
        </>
    );
};
